/**
 * intentDocExtractor — Intent Prefill IP6.
 *
 * Deterministic regex parser used by `suggestProjectIntent` to populate
 * the `document-extracted` tier. Scans `textContent` from documents tagged
 * with sales-/deal-/brief tags for recognisable intent phrases and emits
 * a partial {workloadProfile, nuclearInterest, priority} shape plus the
 * matched phrase per field (for the chip rationale).
 *
 * Why deterministic / not an LLM:
 *   - Free (no token cost on the wizard's open path).
 *   - Auditable — the matched phrase rides through into the chip
 *     tooltip so the user sees the exact evidence we acted on and
 *     can reject false positives without guessing.
 *   - Robust — regex hits are idempotent across rebuilds; an LLM
 *     would be a flake source on this UX path.
 *
 * Conservative by design — we'd rather under-suggest than mis-suggest.
 * False positives are tolerable because the rationale chip surfaces the
 * matched phrase and the user can decline. False negatives are also
 * tolerable: the suggester falls through to the next tier
 * (site-context / archetype-default) and the user still gets a useful
 * pre-fill.
 */

import type { SiteDocument } from '../types/siteRag';
import type { WorkloadProfile } from './workloadProfile';

/* ── EX-7: locus-keyword adjacency for the deck matchers ──────────────
 *
 * The S1 matchers (`matchSiteAcreage`, `matchPowerRequirementMW`,
 * `matchTimeline`) returned the FIRST unit-bearing figure in the deck.
 * That grabs the WRONG number when the deck leads with a backdrop figure
 * before the headline ("the 332-acre county tract … the 800-acre campus";
 * "5,000 MW regional queue … our 350 MW campus"). Both figures carry the
 * unit, so the range/unit guards cannot tell them apart — the only signal
 * is which figure sits next to the SITE / CAMPUS / project keyword.
 *
 * `pickKeywordAdjacentMatch` re-ranks the regex matches: a figure within
 * `EX7_LOCUS_WINDOW` chars of a context keyword wins (nearest keyword
 * breaks ties). When NO match is keyword-adjacent it falls back to the
 * first in-bounds match — identical to the pre-EX-7 behaviour, so a deck
 * with no context keyword is never made worse. Conservative by design
 * (the module's stated contract: under-suggest, never mis-suggest).
 */
const EX7_LOCUS_WINDOW = 80;

/** Context keywords that mark "this figure is THE SITE's size". */
const ACREAGE_LOCUS_KEYWORDS: readonly string[] = [
  'site', 'campus', 'parcel', 'development', 'project', 'facility', 'datacenter', 'data center', 'property',
];
/** Context keywords that mark "this figure is THE PROJECT's power". */
const POWER_LOCUS_KEYWORDS: readonly string[] = [
  'campus', 'site', 'project', 'datacenter', 'data center', 'facility', 'load', 'demand', 'capacity', 'it load',
];

/**
 * EX-7 — re-rank `[{ value, index }]` candidates by proximity to a
 * context keyword in `corpus`. Returns the keyword-adjacent candidate
 * nearest a keyword; when none is adjacent, returns the first candidate
 * (pre-EX-7 fallback). Pure / synchronous.
 */
function pickKeywordAdjacentMatch<T extends { index: number }>(
  candidates: readonly T[],
  corpus: string,
  keywords: readonly string[],
): T | null {
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0]!;
  // Pre-compute keyword positions once.
  const kwPositions: number[] = [];
  for (const kw of keywords) {
    let from = 0;
    for (;;) {
      const p = corpus.indexOf(kw, from);
      if (p < 0) break;
      kwPositions.push(p);
      from = p + kw.length;
    }
  }
  if (kwPositions.length === 0) return candidates[0]!;
  let best: { cand: T; dist: number } | null = null;
  for (const cand of candidates) {
    let nearest = Infinity;
    for (const kp of kwPositions) {
      const d = Math.abs(kp - cand.index);
      if (d < nearest) nearest = d;
    }
    if (nearest <= EX7_LOCUS_WINDOW && (!best || nearest < best.dist)) {
      best = { cand, dist: nearest };
    }
  }
  // Keyword-adjacent winner, else first candidate (pre-EX-7 behaviour).
  return best ? best.cand : candidates[0]!;
}

export type NuclearInterestExtract = 'primary' | 'considering' | 'no';
export type PriorityExtract = 'cost' | 'speed' | 'sustainability' | 'resilience';

export interface DocIntentExtraction {
  workloadProfile?: WorkloadProfile;
  nuclearInterest?: NuclearInterestExtract;
  priority?: PriorityExtract;
  /**
   * S1 — exact power requirement in MW parsed from the deck (e.g.
   * "350 MW campus"). Clamped to the form bounds (0.1–10000) by the
   * matcher; out-of-range hits are dropped. Absent ⇒ no MW phrase found.
   */
  powerRequirementMW?: number;
  /**
   * S1 — usable site acreage parsed from the deck (e.g. "120-acre site").
   * Drives the deterministic power-band tier downstream. Absent ⇒ no
   * acreage phrase found.
   */
  siteAcreage?: number;
  /**
   * S1 — target in-service date / COD parsed from the deck. Either an exact
   * ISO date (`inServiceDate`) when a full date / "Q_ YYYY" / month-year was
   * matched, or a coarse `timeline` bucket when only a relative horizon
   * ("within 2 years") was found. Both absent ⇒ no timeline phrase.
   */
  inServiceDate?: string;
  timeline?: 'under-2yr' | '2-5yr' | 'over-5yr';
  /** Short audit string per field, e.g. "BTM nuclear" — surfaced in the
   *  chip tooltip so the user sees the exact evidence we matched. */
  matchedPhrase?: {
    workloadProfile?: string;
    nuclearInterest?: string;
    priority?: string;
    powerRequirementMW?: string;
    siteAcreage?: string;
    timeline?: string;
  };
}

/** Documents that look like sales / business briefs — only these are
 *  scanned for intent phrases. A plain technical PDF would never carry
 *  these tags, so we won't pull "nuclear safety report" mentions into a
 *  nuclear-primary suggestion. */
export const SALES_DECK_TAGS: ReadonlySet<string> = new Set([
  'sales-deck',
  'deal-packet',
  'project-brief',
  'one-pager',
]);

/** Build the merged-lowercased corpus from a set of docs. Exported for
 *  testing — caller-facing entry point is `extractIntentFromDocs`. */
export function buildSalesDeckCorpus(docs: SiteDocument[] | undefined): string {
  if (!docs || docs.length === 0) return '';
  const decks = docs.filter(d => Array.isArray(d.tags) && d.tags.some(t => SALES_DECK_TAGS.has(t)));
  if (decks.length === 0) return '';
  return decks
    .map(d => (typeof d.textContent === 'string' ? d.textContent : ''))
    .join('\n')
    .toLowerCase();
}

/** Match the first nuclear-interest signal in a lowercased corpus. */
export function matchNuclearInterest(corpus: string): { value: NuclearInterestExtract; phrase: string } | null {
  if (!corpus) return null;
  // Primary — explicit BTM-nuclear or SMR language.
  if (/(behind[\s.-]the[\s.-]meter|btm)[^.]{0,40}nuclear/.test(corpus)) {
    return { value: 'primary', phrase: 'BTM nuclear' };
  }
  if (/small[\s.-]modular[\s.-]reactor|\bsmr\b/.test(corpus)) {
    return { value: 'primary', phrase: 'SMR' };
  }
  // Considering — "nuclear under consideration" / future-tense language.
  if (/nuclear[^.]{0,40}(consider|future|optional|evaluat|explor)/.test(corpus)) {
    return { value: 'considering', phrase: 'nuclear under consideration' };
  }
  // No — explicit exclusion.
  if (/no[\s.-]nuclear|exclude[s]?[\s.-]nuclear|nuclear[\s.-]not[\s.-](required|considered)/.test(corpus)) {
    return { value: 'no', phrase: 'nuclear excluded' };
  }
  return null;
}

/** Match the first priority signal in a lowercased corpus. Order
 *  matters — the spec calls out that concrete priority phrases (cost,
 *  speed) should win over softer ESG copy when both appear. */
export function matchPriority(corpus: string): { value: PriorityExtract; phrase: string } | null {
  if (!corpus) return null;
  if (/low[\s.-]?cost|cost[\s.-]optimi[sz]e|cheapest|low(est)?[\s.-]lcoe/.test(corpus)) {
    return { value: 'cost', phrase: 'lowest cost' };
  }
  if (/fast[\s.-]?track|aggressive[\s.-]timeline|q[1-4][\s.-]?\d{4}[\s.-]target|6[\s.-]?month|time[\s.-]to[\s.-]power/.test(corpus)) {
    return { value: 'speed', phrase: 'speed / time-to-power' };
  }
  if (/100[\s.-]?%[\s.-]?renewable|carbon[\s.-]neutral|net[\s.-]zero|sustainab/.test(corpus)) {
    return { value: 'sustainability', phrase: 'sustainability target' };
  }
  if (/99\.99|n\+[12]|tier[\s.-]iv|resilien|redundan/.test(corpus)) {
    return { value: 'resilience', phrase: 'resilience / Tier IV' };
  }
  return null;
}

/** Match the first workload-profile signal in a lowercased corpus. More
 *  specific patterns first so "AI training" wins over generic
 *  "hyperscale" when both appear. */
export function matchWorkloadProfile(corpus: string): { value: WorkloadProfile; phrase: string } | null {
  if (!corpus) return null;
  if (/ai[\s.-]training|gpu[\s.-]cluster|llm[\s.-]training|frontier[\s.-]model/.test(corpus)) {
    return { value: 'ai-training', phrase: 'AI training' };
  }
  if (/ai[\s.-]inference|inference[\s.-]workload|low[\s.-]latency[\s.-]inference/.test(corpus)) {
    return { value: 'ai-inference', phrase: 'AI inference' };
  }
  if (/\bhpc\b|high[\s.-]performance[\s.-]computing|supercomput/.test(corpus)) {
    return { value: 'hpc', phrase: 'HPC' };
  }
  if (/hyperscale|hyperscaler|colocation|\bcolo\b/.test(corpus)) {
    return { value: 'colocation', phrase: 'hyperscale / colocation' };
  }
  if (/enterprise[\s.-]workload|enterprise[\s.-]it/.test(corpus)) {
    return { value: 'enterprise', phrase: 'enterprise workload' };
  }
  if (/edge[\s.-]computing|edge[\s.-]site|micro[\s.-]edge/.test(corpus)) {
    return { value: 'edge', phrase: 'edge computing' };
  }
  return null;
}

/** Form bounds for an exact MW figure (shared with the suggester guard). */
const POWER_FORM_MIN_MW = 0.1;
const POWER_FORM_MAX_MW = 10000;

/**
 * S1 — match an explicit power requirement in MW. Accepts "350 MW",
 * "350-MW", "350 megawatt(s)". Out-of-range hits are skipped so a
 * "5000 MW grid" backdrop figure can't poison the suggestion.
 *
 * EX-7 — among in-bounds matches, prefer the figure adjacent to a
 * project/campus/load keyword (so a "5,000 MW regional queue … 350 MW
 * campus" deck binds to 350, not 5000). When no match is keyword-adjacent
 * it falls back to the FIRST in-bounds match (pre-EX-7 behaviour — decks
 * that just lead with the headline number are unaffected).
 */
export function matchPowerRequirementMW(corpus: string): { value: number; phrase: string } | null {
  if (!corpus) return null;
  const re = /(\d{1,5}(?:\.\d+)?)\s*[-\s]?\s*(?:mw\b|megawatts?\b)/g;
  const candidates: Array<{ value: number; raw: string; index: number }> = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(corpus)) !== null) {
    const mw = Number(m[1]);
    if (Number.isFinite(mw) && mw >= POWER_FORM_MIN_MW && mw <= POWER_FORM_MAX_MW) {
      candidates.push({ value: mw, raw: m[1]!, index: m.index });
    }
  }
  const pick = pickKeywordAdjacentMatch(candidates, corpus, POWER_LOCUS_KEYWORDS);
  return pick ? { value: pick.value, phrase: `${pick.raw} MW` } : null;
}

/**
 * S1 — match a usable site acreage. Accepts "120 acre", "120-acre",
 * "120 acres". In-bounds figures are 0 < acres ≤ 100000 (larger is almost
 * certainly a county/region backdrop, not the site).
 *
 * EX-7 — among in-bounds matches, prefer the figure adjacent to a
 * site/campus/parcel keyword (so a "332-acre county tract … 800-acre
 * campus" deck binds to 800, not the first-mentioned 332). When no match
 * is keyword-adjacent it falls back to the FIRST in-bounds figure
 * (pre-EX-7 behaviour — a single bare "120-acre parcel" is unaffected).
 */
export function matchSiteAcreage(corpus: string): { value: number; phrase: string } | null {
  if (!corpus) return null;
  const re = /(\d{1,6}(?:\.\d+)?)\s*[-\s]?\s*acres?\b/g;
  const candidates: Array<{ value: number; raw: string; index: number }> = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(corpus)) !== null) {
    const acres = Number(m[1]);
    if (Number.isFinite(acres) && acres > 0 && acres <= 100000) {
      candidates.push({ value: acres, raw: m[1]!, index: m.index });
    }
  }
  const pick = pickKeywordAdjacentMatch(candidates, corpus, ACREAGE_LOCUS_KEYWORDS);
  return pick ? { value: pick.value, phrase: `${pick.raw} acres` } : null;
}

/**
 * S1 — match a target in-service date / COD. Prefers an exact anchor
 * (full ISO date, "Q_ YYYY", or "Mon YYYY") and resolves it to a
 * mid-quarter / first-of-month ISO date; falls back to a coarse bucket
 * from relative-horizon language ("within N years"). Returns at most one
 * of `inServiceDate` / `timeline`. Conservative — first clear anchor wins.
 */
export function matchTimeline(
  corpus: string,
): { inServiceDate?: string; timeline?: 'under-2yr' | '2-5yr' | 'over-5yr'; phrase: string } | null {
  if (!corpus) return null;

  // Exact ISO date (yyyy-mm-dd).
  const iso = corpus.match(/\b(20\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])\b/);
  if (iso) {
    return { inServiceDate: `${iso[1]}-${iso[2]}-${iso[3]}`, phrase: iso[0] };
  }

  // "Q3 2028" / "q3-2028" → first day of that quarter's middle month.
  const quarter = corpus.match(/\bq([1-4])[\s.-]?(20\d{2})\b/);
  if (quarter) {
    const q = Number(quarter[1]);
    const month = String((q - 1) * 3 + 2).padStart(2, '0'); // mid-quarter month
    return { inServiceDate: `${quarter[2]}-${month}-01`, phrase: `Q${q} ${quarter[2]}` };
  }

  // "Jan 2028" / "January 2028".
  const monthYear = corpus.match(
    /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*[\s.-]+(20\d{2})\b/,
  );
  if (monthYear) {
    const idx = MONTHS.indexOf(monthYear[1].slice(0, 3));
    if (idx >= 0) {
      return {
        inServiceDate: `${monthYear[2]}-${String(idx + 1).padStart(2, '0')}-01`,
        phrase: `${monthYear[1]} ${monthYear[2]}`,
      };
    }
  }

  // Relative horizon → coarse bucket.
  const within = corpus.match(/within\s+(\d{1,2})\s*(?:years?|yrs?)/);
  if (within) {
    const yrs = Number(within[1]);
    if (Number.isFinite(yrs)) {
      const bucket = yrs < 2 ? 'under-2yr' : yrs <= 5 ? '2-5yr' : 'over-5yr';
      return { timeline: bucket, phrase: `within ${yrs} years` };
    }
  }
  return null;
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * Top-level entry point — scan sales-deck-tagged documents for intent
 * phrases. Returns a partial object; missing fields mean "no signal" and
 * the caller (suggestProjectIntent) falls through to its next tier.
 *
 * Never throws — bad / missing inputs return an empty extraction.
 */
export function extractIntentFromDocs(docs: SiteDocument[] | undefined): DocIntentExtraction {
  const out: DocIntentExtraction = { matchedPhrase: {} };
  const corpus = buildSalesDeckCorpus(docs);
  if (!corpus.trim()) return out;

  const n = matchNuclearInterest(corpus);
  if (n) {
    out.nuclearInterest = n.value;
    out.matchedPhrase!.nuclearInterest = n.phrase;
  }
  const p = matchPriority(corpus);
  if (p) {
    out.priority = p.value;
    out.matchedPhrase!.priority = p.phrase;
  }
  const w = matchWorkloadProfile(corpus);
  if (w) {
    out.workloadProfile = w.value;
    out.matchedPhrase!.workloadProfile = w.phrase;
  }
  const mw = matchPowerRequirementMW(corpus);
  if (mw) {
    out.powerRequirementMW = mw.value;
    out.matchedPhrase!.powerRequirementMW = mw.phrase;
  }
  const ac = matchSiteAcreage(corpus);
  if (ac) {
    out.siteAcreage = ac.value;
    out.matchedPhrase!.siteAcreage = ac.phrase;
  }
  const tl = matchTimeline(corpus);
  if (tl) {
    if (tl.inServiceDate) out.inServiceDate = tl.inServiceDate;
    if (tl.timeline) out.timeline = tl.timeline;
    out.matchedPhrase!.timeline = tl.phrase;
  }
  return out;
}
